import { useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { useLocation } from 'react-router';

/* Three conditions, all of them load-bearing:
   - `typeof window` because this module is evaluated during the prerender pass
     too, where there is no browser to instrument;
   - PROD so local development does not pollute the project's events;
   - a non-empty key, because posthog.init() with an undefined token logs a
     misconfiguration error to every visitor's console and then does nothing
     useful. A production build made without the env var set should be quiet,
     not broken. */
const enabled =
  typeof window !== 'undefined' &&
  import.meta.env.PROD &&
  Boolean(import.meta.env.VITE_POSTHOG_KEY);

if (enabled) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST,
    person_profiles: 'always', // create profiles for anonymous users as well
    /* Captured manually below instead. Client-side routing means the SDK's own
       pageview only ever fires for the first document a visitor loads, so
       leaving it on would under-report every route reached by a router
       navigation. */
    capture_pageview: false,
  });
}

/**
 * One $pageview per route.
 *
 * Fires on the first render as well as on every subsequent navigation, which is
 * what the SDK's automatic capture did for the whole site back when every link
 * was a full page load.
 */
function usePageviews() {
  const { pathname, search } = useLocation();
  /* React 19 StrictMode double-invokes effects in development. Without this the
     same URL is reported twice on every navigation locally. */
  const lastReported = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const url = `${pathname}${search}`;
    if (lastReported.current === url) return;
    lastReported.current = url;

    posthog.capture('$pageview', { $current_url: window.location.href });
  }, [pathname, search]);
}

export function AppProviders({ children }: { children: React.ReactNode }) {
  usePageviews();
  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}
